import React, {forwardRef} from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  placeholder?: string;
  error?: boolean;
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({options, placeholder, error = false, className = '', ...props}, ref) => {
  const baseClasses = [
    'w-full px-4 py-3 border rounded-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none',
    'appearance-none bg-no-repeat bg-right bg-[length:16px] pr-10',
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
      : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
    className,
  ];

  const selectClasses = baseClasses.filter(Boolean).join(' ');

  // Custom dropdown arrow using CSS
  const backgroundImage = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`;

  return (
    <div className="relative">
      <select
        ref={ref}
        {...props}
        className={selectClasses}
        style={{
          backgroundImage,
          backgroundPosition: 'right 0.75rem center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '1.5em 1.5em',
        }}
        aria-invalid={error}>
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
});

Select.displayName = 'Select';
